import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import { Button, Form, InputGroup, FormControl } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";

function Hospital() {
    const [hospitals, setHospitals] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState([]);

    const navigate = useNavigate();
    const accesstoken = localStorage.getItem("token");

    useEffect(() => {
        getAllHospitals();
    }, []);

    const getAllHospitals = () => {
        axios
            .get(baseurladdress + "hospital/findallactivehospitals", {
                headers: { Authorization: `Bearer ${accesstoken}` },
            })
            .then((res) => {
                if (res.data.status === "Success") {
                    setHospitals(res.data.hospitals);
                }
            })
            .catch((err) => {
                console.error("Error fetching hospitals", err);
            });
    };

    function deleteHospital(hosp_id) {
        Swal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(baseurladdress + `hospital/deletehospital/${hosp_id}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json; charset=UTF-8",
                        Authorization: `Bearer ${accesstoken}`,
                    },
                })
                    .then((response) => response.json())
                    .then((result) => {
                        if (result.status === "success") {
                            Swal.fire({
                                toast: true,
                                position: "top-end",
                                icon: "success",
                                title: "Deleted Successfully",
                                showConfirmButton: false,
                                timer: 1200,
                                timerProgressBar: true,
                            });
                            getAllHospitals();
                        } else if (result.status === "Not Found") {
                            Swal.fire({
                                icon: "error",
                                title: "Not Found",
                                text: "Hospital not found or already deleted.",
                            });
                        } else {
                            Swal.fire({
                                icon: "error",
                                title: "Failed",
                                text: "Unable to delete hospital. Please try again.",
                            });
                        }
                    })
                    .catch((error) => {
                        console.error("Delete hospital error:", error);
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: "Something went wrong while deleting hospital.",
                        });
                    });
            }
        });
    }

    function AddHospital() {
        navigate("/addhospital");
    }

    function editHospital(hosp_id) {
        navigate("/edithospital/" + hosp_id);
    }

    // Define columns for TanStack Table
    const columns = useMemo(
        () => [
            {
                accessorKey: "index",
                header: "Sl.No",
                size: 60,
                cell: ({ row }) => row.index + 1,
            },
            {
                accessorKey: "hosp_name",
                header: "Name",
                size: 150,
            },
            {
                accessorKey: "hosp_code",
                header: "Code",
                size: 100,
            },
            {
                accessorKey: "hosp_contact",
                header: "Contact",
                size: 120,
            },
            {
                accessorKey: "hosp_address",
                header: "Address",
                size: 200,
            },
            {
                accessorKey: "district_name",
                header: "District",
                size: 120,
            },
            {
                accessorKey: "staff_name",
                header: "Served By",
                size: 150,
            },
            {
                accessorKey: "actions",
                header: "Actions",
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <Icon.PencilSquare
                            className="text-primary"
                            onClick={() => editHospital(row.original.hosp_id)}
                            style={{ cursor: "pointer" }}
                        />
                        <Icon.Trash
                            className="text-danger"
                            onClick={() => deleteHospital(row.original.hosp_id)}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                ),
                size: 80,
            },
        ],
        []
    );

    // Initialize TanStack Table
    const table = useReactTable({
        data: hospitals,
        columns,
        state: {
            globalFilter,
            sorting,
        },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    return (
        <div className="content-wrap">
            <div className="main">
                <div className="container-fluid">
                    <section id="main-content">
                        <div className="card">
                            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
                                <h6 className="card-title mb-0">HOSPITAL DETAILS</h6>
                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={(e) => {
                                        AddHospital();
                                        e.currentTarget.blur();
                                    }}
                                    className="d-flex align-items-center"
                                >
                                    <Icon.Plus className="me-1" />
                                    Add
                                </Button>
                            </div>

                            <div className="card-body">
                                {/* Compact Search and Controls Bar */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted small">Show</span>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "70px" }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => {
                                                table.setPageSize(Number(e.target.value));
                                            }}
                                        >
                                            {[5, 10, 20, 50].map((pageSize) => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <span className="text-muted small">entries</span>
                                    </div>

                                    <InputGroup style={{ width: "250px" }}>
                                        <InputGroup.Text>
                                            <Icon.Search size={14} />
                                        </InputGroup.Text>
                                        <FormControl
                                            size="sm"
                                            placeholder="Search..."
                                            value={globalFilter ?? ""}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </InputGroup>
                                </div>

                                {/* Table */}
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm align-middle" >
                                        <thead className="table-light">
                                            {table.getHeaderGroups().map((headerGroup) => (
                                                <tr key={headerGroup.id}>
                                                    {headerGroup.headers.map((header) => (
                                                        <th
                                                            key={header.id}
                                                            style={{ width: header.getSize() }}
                                                            className="text-nowrap"
                                                        >
                                                            {header.isPlaceholder ? null : (
                                                                <div
                                                                    {...{
                                                                        className: header.column.getCanSort()
                                                                            ? "cursor-pointer select-none d-flex align-items-center"
                                                                            : "",
                                                                        onClick: header.column.getToggleSortingHandler(),
                                                                    }}
                                                                >
                                                                    {flexRender(
                                                                        header.column.columnDef.header,
                                                                        header.getContext()
                                                                    )}
                                                                    {header.column.getCanSort() && (
                                                                        <span className="ms-1">
                                                                            {{
                                                                                asc: <Icon.ChevronUp size={12} />,
                                                                                desc: <Icon.ChevronDown size={12} />,
                                                                            }[header.column.getIsSorted()] ?? (
                                                                                    <Icon.ChevronExpand size={12} className="text-muted" />
                                                                                )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                        </thead>
                                        <tbody>
                                            {table.getRowModel().rows.length > 0 ? (
                                                table.getRowModel().rows.map((row) => (
                                                    <tr key={row.id}>
                                                        {row.getVisibleCells().map((cell) => (
                                                            <td
                                                                key={cell.id}
                                                                style={{
                                                                    
                                                                    textAlign: "left",
                                                                    verticalAlign: "middle",
                                                                }}
                                                            >

                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={columns.length} className="text-center text-muted py-4">
                                                        No hospitals found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Compact Pagination */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted small">
                                        Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
                                        {Math.min(
                                            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                            table.getFilteredRowModel().rows.length
                                        )}{" "}
                                        of {table.getFilteredRowModel().rows.length} entries
                                    </div>

                                    <div className="btn-group btn-group-sm">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            <Icon.ChevronDoubleLeft size={14} />
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            <Icon.ChevronLeft size={14} />
                                        </Button>
                                        <Button variant="outline-secondary" disabled>
                                            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            <Icon.ChevronRight size={14} />
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            <Icon.ChevronDoubleRight size={14} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Hospital;